import type { MemoryEntry } from '@proj-airi/server-sdk-shared'

import { errorMessageFrom } from '@moeru/std'
import { memoryEntrySchema } from '@proj-airi/server-sdk-shared'
import { useLocalStorageManualReset } from '@proj-airi/stage-shared/composables'
import { useDocumentVisibility, useIntervalFn, useLocalStorage, useOnline, watchDebounced } from '@vueuse/core'
import { isEqual } from 'es-toolkit'
import { defineStore, storeToRefs } from 'pinia'
import { safeParse } from 'valibot'
import { computed, onScopeDispose, ref, watch } from 'vue'

import { documentSyncRepo } from '../database/repos/document-sync.repo'
import { authedFetch } from '../libs/auth-fetch'
import { createDocumentSyncClient, synchronize } from '../libs/document-sync'
import { applyMemoryChanges, splitMemories } from '../libs/memory-sync'
import { SERVER_URL } from '../libs/server'
import { useAuthStore } from './auth'

export type { MemoryEntry } from '@proj-airi/server-sdk-shared'

const emptyEntries: readonly MemoryEntry[] = Object.freeze([])

/**
 * Normalizes a name to the bounded key that memory tools and synchronization use.
 *
 * @example
 * memoryName('Favorite Game!')
 * // => 'favorite-game'
 */
export function memoryName(name: string) {
  return name.trim().toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '').slice(0, 64).replace(/-+$/g, '')
}

/**
 * Long-term memory as a small index and one entry per fact, kept by the character itself.
 *
 * Use when:
 * - A run reads the index or an entry, writes or forgets a fact, or Settings lists the memories by card.
 *
 * Expects:
 * - Callers pass the character card of the run. It reads its own memories and the general ones.
 * - Only the owner makes a memory general, in Settings. A run writes and forgets only its own card's memories.
 *
 * Returns:
 * - Whether memory is on, entries, the index for a card, and actions that change entries.
 */
export const useMemoryStore = defineStore('memory', () => {
  const { userId } = storeToRefs(useAuthStore())
  const enabled = useLocalStorageManualReset<boolean>('memory/enabled', true)
  const anonymousEntries = useLocalStorage<MemoryEntry[]>('memory/entries', [])
  const accounts = useLocalStorage<Map<string, MemoryEntry[]>>('memory/accounts', new Map())
  const cloudSyncEnabled = useLocalStorageManualReset<boolean>('memory/cloud-sync-enabled', import.meta.env.VITE_MEMORY_SYNC_ENABLED === 'true')
  const entries = computed<readonly MemoryEntry[]>(() => userId.value === 'local' ? anonymousEntries.value : accounts.value.get(userId.value) ?? emptyEntries)
  const syncStatus = ref<'local' | 'syncing' | 'synced' | 'error' | 'refused'>('local')
  const syncError = ref<string>()
  const lastSyncedAt = ref<number>()
  const online = useOnline()
  const visibility = useDocumentVisibility()

  // Account generation invalidates an in-flight response, including a switch away and back to the same account.
  let generation = 0
  let disposed = false
  let activeSynchronization: Promise<void> | undefined
  let queued = false
  const client = createDocumentSyncClient({ serverUrl: SERVER_URL, path: '/api/v1/memories', fetch: authedFetch, requestTimeoutMs: 3000 })

  function replaceEntries(next: MemoryEntry[]) {
    if (isEqual(entries.value, next))
      return
    if (userId.value === 'local')
      anonymousEntries.value = next
    else
      accounts.value = new Map(accounts.value).set(userId.value, next)
  }

  async function runSynchronization() {
    const ownerId = userId.value
    const version = generation
    const isCurrent = () => !disposed && generation === version && userId.value === ownerId && cloudSyncEnabled.value
    if (ownerId === 'local' || !cloudSyncEnabled.value || !online.value)
      return

    // Web Locks serialize the IndexedDB sync cursor across Electron windows and browser tabs.
    try {
      await navigator.locks.request(`airi:memory-sync:${ownerId}`, async () => {
        if (!isCurrent())
          return
        syncStatus.value = 'syncing'
        syncError.value = undefined
        try {
          let state = await documentSyncRepo.getState('memories', ownerId) ?? { documents: {} }
          let rejected = false
          const result = await synchronize({
            client,
            state,
            isCurrent,
            async saveState(next) {
              state = next
              await documentSyncRepo.saveState('memories', ownerId, next)
            },
            readLocal: () => splitMemories(entries.value),
            async applyLocal(changes) {
              const applied = applyMemoryChanges(entries.value, changes, state)
              rejected ||= applied.rejected.length > 0
              replaceEntries(applied.entries)
              return { rejected: applied.rejected }
            },
          })
          if (!isCurrent())
            return
          syncStatus.value = result.refused.length > 0 || rejected ? 'refused' : 'synced'
          lastSyncedAt.value = Date.now()
        }
        catch (error) {
          if (isCurrent()) {
            syncStatus.value = 'error'
            syncError.value = errorMessageFrom(error)
          }
        }
      })
    }
    catch (error) {
      if (isCurrent()) {
        syncStatus.value = 'error'
        syncError.value = errorMessageFrom(error)
      }
    }
  }

  /** Exchanges pending facts and remote changes. Failures retain the local cache and its unsent edits. */
  async function synchronizeMemories() {
    if (disposed)
      return
    if (activeSynchronization) {
      queued = true
      return activeSynchronization
    }
    activeSynchronization = (async () => {
      do {
        queued = false
        await runSynchronization()
        if (disposed)
          break
      } while (queued)
    })()
    try {
      await activeSynchronization
    }
    finally {
      activeSynchronization = undefined
    }
  }

  // The store owns these observers. Disposal stops scheduling and makes old responses ineligible to commit.
  const polling = useIntervalFn(synchronizeMemories, 15_000, { immediate: false })
  watch([userId, cloudSyncEnabled], () => {
    generation += 1
    lastSyncedAt.value = undefined
    syncStatus.value = 'local'
    syncError.value = undefined
    void synchronizeMemories()
  }, { flush: 'sync' })
  watch([userId, cloudSyncEnabled, online, visibility], () => {
    if (userId.value !== 'local' && cloudSyncEnabled.value && online.value && visibility.value === 'visible') {
      polling.resume()
      void synchronizeMemories()
    }
    else {
      polling.pause()
    }
  }, { immediate: true })
  watchDebounced(entries, synchronizeMemories, { debounce: 500, maxWait: 2000, deep: true })
  onScopeDispose(() => {
    disposed = true
    generation += 1
  })

  const sorted = computed(() => [...entries.value].sort((left, right) => left.name.localeCompare(right.name)))

  /** General memories and the persona's own. */
  function visibleTo(personaId: string) {
    return sorted.value.filter(entry => !entry.persona || entry.persona === personaId)
  }

  /** The index for a card: its own memories, then general ones, each as a name and its description. */
  function indexFor(personaId: string) {
    const line = (entry: MemoryEntry) => `- ${entry.name}: ${entry.description}`
    const byName = (left: MemoryEntry, right: MemoryEntry) => left.name.localeCompare(right.name)
    const own = sorted.value.filter(entry => entry.persona === personaId).sort(byName).map(line)
    const general = sorted.value.filter(entry => !entry.persona).sort(byName).map(line)
    return [
      ...(own.length ? [`Your own:\n${own.join('\n')}`] : []),
      ...(general.length ? [`General:\n${general.join('\n')}`] : []),
    ].join('\n')
  }

  /** Reads an entry by name. The persona's own entry wins over a general one with the same name. */
  function read(name: string, personaId: string) {
    const key = memoryName(name)
    const matches = visibleTo(personaId).filter(entry => entry.name === key)
    return matches.find(entry => entry.persona === personaId) ?? matches[0]
  }

  /** Creates or replaces an entry of the card by name. Returns the stored entry, or undefined for an empty name or body. */
  function write(entry: Pick<MemoryEntry, 'name' | 'description' | 'body'>, personaId: string) {
    const name = memoryName(entry.name)
    if (!name || !entry.body.trim())
      return undefined
    const next: MemoryEntry = { name, description: entry.description.trim(), body: entry.body.trim(), persona: personaId, updatedAt: Date.now() }
    if (!safeParse(memoryEntrySchema, next).success)
      return undefined
    if (entries.value.length >= 256 && !entries.value.some(existing => existing.name === name && existing.persona === personaId))
      return undefined
    replaceEntries([...entries.value.filter(existing => existing.name !== name || existing.persona !== personaId), next])
    return next
  }

  /** Makes a card's memory general, so every card reads it. A general memory with the same name gives way. */
  function makeGeneral(entry: Pick<MemoryEntry, 'name' | 'persona'>) {
    const source = entries.value.find(existing => existing.name === entry.name && existing.persona === entry.persona)
    if (!source?.persona)
      return
    const { persona: _card, ...general } = source
    replaceEntries([...entries.value.filter(existing => existing !== source && (existing.name !== source.name || existing.persona)), { ...general, updatedAt: Date.now() }])
  }

  /** Removes one entry, from a tool or from Settings. */
  function remove(entry: Pick<MemoryEntry, 'name' | 'persona'>) {
    replaceEntries(entries.value.filter(existing => existing.name !== entry.name || existing.persona !== entry.persona))
  }

  /** The owner can edit a fact without changing its name or sharing scope. A model cannot call this operation. */
  function update(entry: Pick<MemoryEntry, 'name' | 'persona'>, fields: Pick<MemoryEntry, 'description' | 'body'>) {
    const current = entries.value.find(existing => existing.name === entry.name && existing.persona === entry.persona)
    if (!current)
      return false
    const next: MemoryEntry = { ...current, description: fields.description.trim(), body: fields.body.trim(), updatedAt: Date.now() }
    if (!safeParse(memoryEntrySchema, next).success)
      return false
    replaceEntries(entries.value.map(existing => existing === current ? next : existing))
    return true
  }

  /** Forgets an entry of the card by name. General memories belong to the owner, so a run cannot forget them. */
  function forget(name: string, personaId: string) {
    const key = memoryName(name)
    const target = entries.value.find(entry => entry.name === key && entry.persona === personaId)
    if (!target)
      return false
    remove(target)
    return true
  }

  function resetState() {
    enabled.reset()
    replaceEntries([])
  }

  return {
    enabled,
    cloudSyncEnabled,
    syncStatus,
    syncError,
    lastSyncedAt,
    synchronizeMemories,
    entries: sorted,
    visibleTo,
    indexFor,
    read,
    write,
    makeGeneral,
    forget,
    remove,
    update,
    resetState,
  }
})
